import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth, apiCall } from '../AuthContext';
import { LayoutDashboard, Settings, Package, LogOut, Menu, X, Layers, FileText, List, HelpCircle, Video, ClipboardList, Shield, PlayCircle, Users, UploadCloud, Tag, Gift, Store, Megaphone, Lightbulb, Sparkles, ShieldCheck } from 'lucide-react';
import ImpersonationBanner from './ImpersonationBanner';
import AnnouncementBanner from './AnnouncementBanner';

const BASE_USER_NAV = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/quick-list', label: 'Quick List', icon: Sparkles },
  { path: '/product-form', label: 'Product Form', icon: FileText },
  { path: '/bulk-upload', label: 'Bulk Upload', icon: UploadCloud },
  { path: '/product-list', label: 'Products', icon: List },
  { path: '/batch-management', label: 'Batches', icon: Package },
  { path: '/configuration', label: 'Integrations', icon: Settings },
  { path: '/label-designer', label: 'Label Designer', icon: Tag },
  { path: '/rewards', label: 'Refer & Earn', icon: Gift },
  { path: '/feature-suggestions', label: 'Feature Suggestions', icon: Lightbulb },
];
const LEARNING_VIDEOS_ITEM = { path: '/learning-videos', label: 'Learning Videos', icon: PlayCircle };

const adminNavItems = [
  { path: '/admin-users', label: 'Customers', icon: Users },
  { path: '/admin-resellers', label: 'Resellers', icon: Store },
  { path: '/admin-cms', label: 'Announcements', icon: Megaphone },
  { path: '/admin-faq', label: 'FAQ Manager', icon: HelpCircle },
  { path: '/admin-videos', label: 'Video Manager', icon: Video },
  { path: '/shopify-readiness', label: 'App Store Readiness', icon: ShieldCheck },
  { path: '/privacy-policy', label: 'Policies', icon: Shield },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [learningVisible, setLearningVisible] = React.useState(false);

  React.useEffect(() => {
    apiCall('/api/app-settings', { method: 'GET' })
      .then(r => setLearningVisible(!!r.data?.learning_videos_visible))
      .catch(() => setLearningVisible(false));
  }, []);

  const isAdmin = user?.is_admin;
  // Admin always sees Learning Videos; regular users only when enabled
  const userNavItems = [...BASE_USER_NAV, ...((isAdmin || learningVisible) ? [LEARNING_VIDEOS_ITEM] : [])];
  const allNavItems = [...userNavItems, ...(isAdmin ? adminNavItems : [])];
  const currentLabel = allNavItems.find(n => n.path === location.pathname)?.label || 'Dashboard';

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" data-testid="app-layout">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-[#0F172A] transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center gap-3 h-16 px-6 border-b border-white/10">
          <div className="w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center">
            <img src="/logo.png" alt="XLabelSync" className="w-8 h-8 object-cover" />
          </div>
          <span className="text-white font-bold text-lg tracking-tight">XLabelSync</span>
        </div>
        <nav className="p-4 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
          {/* User nav */}
          {userNavItems.map(item => {
            const Icon = item.icon;
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${active ? 'bg-[#0052FF] text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </Link>
            );
          })}

          {/* Admin section */}
          {isAdmin && (
            <>
              <div className="pt-4 pb-2 px-4">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-600">Admin</p>
              </div>
              {adminNavItems.map(item => {
                const Icon = item.icon;
                const active = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${active ? 'bg-[#0052FF] text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                );
              })}
            </>
          )}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <div className="px-4 py-2 mb-2">
            <p className="text-white text-sm font-medium truncate">{user?.username || user?.email}</p>
            <p className="text-slate-500 text-xs truncate">{user?.email}</p>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="flex items-center gap-3 w-full px-4 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 lg:ml-64">
        <ImpersonationBanner />
        <header className="sticky top-0 z-20 backdrop-blur-xl bg-white/80 border-b border-slate-200 h-16 flex items-center px-6">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden mr-4 p-1" data-testid="mobile-menu-toggle">
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <h1 className="text-lg font-semibold text-[#0F172A]">{currentLabel}</h1>
          <div className="ml-auto flex items-center gap-3">
            {isAdmin && (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#0052FF] text-white">Admin</span>
            )}
          </div>
        </header>
        <main className="p-6 lg:p-8">
          <AnnouncementBanner />
          {/*
            Keying the page wrapper by location.key forces React to remount
            the active page whenever the user navigates — including clicking
            the same sidebar link they're already on. This gives the user a
            "fresh start" without needing to bounce to another page first.
          */}
          <div key={location.key}>{children}</div>
        </main>
      </div>
    </div>
  );
}
